import React, { Fragment, useEffect, useRef, useState } from "react"

import { FormikValues, useField, useFormikContext } from "formik"
import { usePopper } from "react-popper"
import { Portal } from "react-portal"
import { gql, useQuery } from "urql"

import { Combobox, Transition } from "@headlessui/react"
import { ChevronUpDownIcon } from "@heroicons/react/20/solid"
import { XMarkIcon } from "@heroicons/react/24/outline"

import { SelectOption } from "~tailwindui/types/SelectOption"

import { ErrorIcon, WithLoadingIndicator } from "~tailwindui/Basics"

import classNames from "src/classNames"

export type AutocompleteSelectProps = {
  name: string
  label: string
  resource: string
  searchColumn?: string
  defaultValue?: Selected
  multi?: boolean
  clearable?: boolean
  subfield?: boolean
  className?: string
  creatable?: boolean
  valueColumn?: string
  portal?: boolean
  afterChange?: (val: Selected) => void
}

type Selected = SelectOption[] | SelectOption | undefined

const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  name,
  label,
  resource,
  searchColumn = "name",
  valueColumn = "id",
  defaultValue,
  multi = false,
  clearable = false,
  subfield = false,
  className = "",
  creatable = false,
  portal = false,
  afterChange,
}) => {
  const [queryString, setQueryString] = useState("")
  const [debouncedQueryString, setDebouncedQueryString] = useState(queryString)
  const inputRef = useRef<HTMLInputElement>()

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQueryString(queryString)
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [queryString])

  const [result] = useQuery({
    query: autocompleteQuery,
    variables: {
      resource,
      searchColumn,
      match: debouncedQueryString,
      valueColumn,
    },
  })

  const [field, meta] = useField({ name })
  const { setFieldValue } = useFormikContext<FormikValues>()
  const error = meta.error ? meta.error : ""

  const [selected, setSelected] = useState<Selected>(
    defaultValue || (multi ? [] : undefined)
  )

  const popperElRef = useRef(null)
  const [targetElement, setTargetElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const { styles, attributes, update } = usePopper(
    targetElement,
    popperElement,
    {
      placement: "bottom-start",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
      ],
    }
  )

  const onChange = selected => {
    setSelected(selected)
    setFieldValue(
      name,
      multi
        ? (selected as SelectOption[]).map(selected => selected?.value)
        : selected?.value
    )
    if (!multi) setQueryString(selected?.label || "")
    if (portal && multi) setTimeout(() => update?.(), 0) // update list positioning

    if (afterChange) afterChange(selected)
  }

  const removeItem = (optionToRemove: SelectOption) => {
    if (multi) {
      const newOpts = (selected as SelectOption[]).filter(
        opt => opt.value !== optionToRemove.value
      )
      onChange(newOpts)
    } else onChange(undefined)
    if (portal && multi) setTimeout(() => update?.(), 0) // update list positioning
  }

  const items = open => {
    let divAttributes = {}
    if (portal) {
      divAttributes = {
        ref: popperElRef,
        style: { zIndex: 100, ...styles.popper },
        ...attributes.popper,
      }
    }

    const content = (
      <div {...divAttributes}>
        <Transition
          show={open}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          beforeEnter={() => setPopperElement(popperElRef.current)}
          afterLeave={() => setPopperElement(null)}
        >
          <Combobox.Options
            className={classNames(
              portal ? "w-max" : "w-full",
              "absolute z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            )}
          >
            {queryString.length > 0 && creatable && (
              <Combobox.Option
                value={{ value: queryString, label: queryString }}
                key="create"
                className={({ active }) =>
                  classNames(
                    "relative cursor-default select-none py-2 px-3",
                    active ? "bg-sky-400 text-white" : "text-gray-900"
                  )
                }
              >
                <span className="pr block">Add "{queryString}"</span>
              </Combobox.Option>
            )}
            <WithLoadingIndicator result={result}>
              {({ data: { options } }) =>
                options.length === 0 && !creatable ? (
                  <div className="min-w-max py-2 px-3 italic">
                    No results found
                  </div>
                ) : (
                  options.map(option => {
                    const isSelected =
                      (multi && field.value.includes(option.value)) ||
                      (!multi && field.value === option.value)
                    return (
                      <Combobox.Option
                        key={option.value}
                        value={option}
                        className={({ active }) =>
                          classNames(
                            "relative min-w-max cursor-default select-none py-2 px-3",
                            isSelected
                              ? "bg-sky-600 text-white"
                              : active
                              ? "bg-sky-400 text-white"
                              : "text-gray-900"
                          )
                        }
                      >
                        <span className="block pr-4">{option.label}</span>
                      </Combobox.Option>
                    )
                  })
                )
              }
            </WithLoadingIndicator>
          </Combobox.Options>
        </Transition>
      </div>
    )

    if (portal) {
      return <Portal>{content}</Portal>
    } else {
      return content
    }
  }

  return (
    <div className={classNames("mb-3", subfield && "ml-8")}>
      <Combobox
        as="div"
        multiple={multi || undefined}
        value={selected as any}
        onChange={onChange}
      >
        {({ open }) => (
          <>
            <label
              htmlFor={name}
              className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
            >
              {label}
            </label>

            {multi && (
              <div className="mb-1 flex flex-auto flex-wrap">
                {(selected as SelectOption[]).map(option => (
                  <div
                    key={option.value}
                    className="m-[1px] mb-1 flex items-center justify-center rounded border border-sky-300 bg-sky-100 py-[.2rem] px-1 font-medium text-sky-700 "
                  >
                    <div className="max-w-full flex-initial text-xs font-normal leading-none">
                      {option.label}
                    </div>
                    <div
                      className="flex flex-auto flex-row-reverse"
                      onClick={() => removeItem(option)}
                    >
                      <XMarkIcon
                        className="h-4 w-4 cursor-pointer hover:text-sky-400"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className={classNames("relative min-w-[160px]", className)}>
              <Combobox.Button as="div" ref={setTargetElement}>
                {({ open }) => (
                  <div className="relative">
                    <Combobox.Input
                      ref={inputRef}
                      className="w-full truncate rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6"
                      onChange={event => {
                        if (event.target.value)
                          setQueryString(event.target.value)
                      }}
                      onClick={e => {
                        if (open) e.stopPropagation()
                        e.target.value = queryString
                      }}
                      key={selected?.toString()}
                      displayValue={() =>
                        multi ? "" : (selected as SelectOption)?.label
                      }
                      value={queryString}
                      placeholder="Search..."
                    />
                    <span className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                      {error ? (
                        <ErrorIcon />
                      ) : clearable &&
                        (multi ? queryString.length > 0 : selected) ? (
                        <span
                          onClick={e => {
                            if (!multi) removeItem(selected as SelectOption)
                            setQueryString("")
                            inputRef.current.value = ""
                            e.stopPropagation()
                          }}
                        >
                          <XMarkIcon
                            className="h-5 w-5 cursor-pointer text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      ) : (
                        <ChevronUpDownIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  </div>
                )}
              </Combobox.Button>

              {items(open)}
            </div>
          </>
        )}
      </Combobox>
    </div>
  )
}

const autocompleteQuery = gql`
  query AutocompleteQuery(
    $resource: String!
    $searchColumn: AutocompleteColumnEnum!
    $match: String!
    $valueColumn: AutocompleteColumnEnum!
  ) {
    options: autocomplete(
      resource: $resource
      searchColumn: $searchColumn
      match: $match
      valueColumn: $valueColumn
    ) {
      label
      value
    }
  }
`

export default AutocompleteSelect
